/**
 * ScreenshareConnection — the `/share` laptop remote's wireless screenshare, a
 * faithful port of spot-client `common/remote-control/screenshare-connection.ts`,
 * re-transported off XMPP.
 *
 * It manages a lib-jitsi-meet {@link JitsiProxyConnectionService} between THIS
 * laptop and the room's Spot-TV: it captures the laptop's screen
 * (`getDisplayMedia`, via `JitsiMeetJS.createLocalTracks(['desktop'])`) and feeds
 * that track into the TV's Jitsi meeting over a P2P link. The proxy's signalling
 * (Jingle IQs) is relayed via the `onSendMessage` callback — in spaces that rides
 * the room DO control channel as `MESSAGES.CLIENT_PROXY_MESSAGE` frames, and the
 * TV pumps inbound replies back through here via {@link processMessage}.
 *
 * Difference from spot: spot passed `jitsiConnection` (its XMPP connection) so the
 * service could read TURN creds. Spaces has no XMPP, so we pass `pcConfig`
 * directly (ProxyConnectionService accepts either; `pcConfig` wins). Dev relies on
 * host/STUN candidates (same-machine/LAN); production must supply real TURN here.
 */

import { log } from '../log.js';

export interface ScreenshareConnectionOptions {
    /** WebRTC ICE config for the P2P link. Dev: host/STUN; prod: must include TURN. */
    pcConfig?: RTCConfiguration;
    /** Relay a proxy signalling message to the Spot-TV (peerJid, data). */
    onSendMessage: (peerJid: string, data: unknown) => void;
    /** Invoked when the connection or the captured desktop track closes. */
    onConnectionClosed: () => void;
}

export class ScreenshareConnection {
    private readonly options: ScreenshareConnectionOptions;
    private active = false;
    private tracks: JitsiLocalTrack[] = [];
    private readonly proxy: JitsiProxyConnectionService;

    constructor(options: ScreenshareConnectionOptions) {
        this.options = options;

        const JitsiMeetJS = window.JitsiMeetJS;

        if (!JitsiMeetJS) {
            throw new Error('lib-jitsi-meet not loaded — call loadLibJitsiMeet first');
        }

        this.proxy = new JitsiMeetJS.ProxyConnectionService({
            pcConfig: options.pcConfig,
            onConnectionClosed: () => this.options.onConnectionClosed(),
            onRemoteStream: () => { /* Spot share is send-only — ignore inbound. */ },
            onSendMessage: (peerJid, data) => this.options.onSendMessage(peerJid, data)
        });
    }

    /** Feed a proxy signalling message received from the TV into the service. */
    processMessage(message: { from: string; data: unknown }): void {
        log.log('screenshare connection got proxy message', { from: message.from });
        this.proxy.processMessage(message);
    }

    /**
     * Capture the laptop screen and start the P2P link to the Spot-TV. `spotId` is
     * the TV's room endpoint id (used as the peer "jid" for proxy signalling).
     */
    async startScreenshare(spotId: string): Promise<void> {
        log.log('screenshare connection starting', { spotId });
        this.active = true;

        const JitsiMeetJS = window.JitsiMeetJS;

        if (!JitsiMeetJS) {
            throw new Error('lib-jitsi-meet not loaded');
        }

        if (!this.tracks.length) {
            // getDisplayMedia — the browser shows its screen/window picker here.
            const tracks = await JitsiMeetJS.createLocalTracks({ devices: ['desktop'] });

            if (!this.active) {
                // Stopped while the picker was open — clean up and bail.
                tracks.forEach(t => void t.dispose());

                return;
            }

            this.tracks = tracks;

            const stopped = JitsiMeetJS.events.track.LOCAL_TRACK_STOPPED;

            for (const track of tracks) {
                track.on(stopped, () => {
                    log.log('screenshare desktop track stopped');
                    if (this.active) {
                        this.options.onConnectionClosed();
                    }
                });
            }
        }

        this.proxy.start(spotId, this.tracks);
    }

    /** Tear down the P2P link and dispose the captured screen track(s). */
    stop(): void {
        log.log('screenshare connection stopping');
        this.active = false;

        try {
            this.proxy.stop();
        } catch (err) {
            log.warn('proxy stop failed', { error: String(err) });
        }

        this.tracks.forEach(t => void t.dispose());
        this.tracks = [];
    }
}
